import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

// Default configuration
const defaultConfig = {
  games: [
    {
      type: "crossword",
      name: "Schwedenrätsel",
      seoText: { title: "", text: "" },
      categories: [
        { type: "tagliches_ratsel", name: "Tägliches Rätsel", showLevelDate: true, levelsCount: 7 },
        { type: "leichtes", name: "Leichtes", showLevelDate: false, levelsCount: 20 },
        { type: "bilderratsel", name: "Bilderrätsel", showLevelDate: false, levelsCount: 20 },
        { type: "halbe_seite", name: "Halbe Seite", showLevelDate: false, levelsCount: 20 },
        { type: "vollformat", name: "Vollformat", showLevelDate: false, levelsCount: 20 },
        { type: "doppelseiter", name: "Doppelseiter", showLevelDate: false, levelsCount: 20 }
      ]
    },
    {
      type: "gridpuzzle",
      name: "Gitterrätsel",
      seoText: { title: "", text: "" },
      categories: [
        { type: "tagliches_ratsel", name: "Tägliches Rätsel", showLevelDate: true, levelsCount: 7 },
        { type: "leichtes", name: "Leichtes", showLevelDate: false, levelsCount: 20 },
        { type: "einfache_ratsel", name: "Einfache Rätsel", showLevelDate: false, levelsCount: 20 },
        { type: "profi_ratsel", name: "Profi Rätsel", showLevelDate: false, levelsCount: 20 },
        { type: "vollformat", name: "Vollformat", showLevelDate: false, levelsCount: 20 }
      ]
    },
    {
      type: "sudoku",
      name: "Sudoku",
      seoText: { title: "", text: "" },
      categories: [
        { type: "tagliches_ratsel", name: "Tägliches Rätsel", showLevelDate: true, levelsCount: 7 },
        { type: "leichtes", name: "Leichtes", showLevelDate: false, levelsCount: 20 },
        { type: "fortgeschrittene_ratsel", name: "Fortgeschrittene Rätsel", showLevelDate: false, levelsCount: 20 },
        { type: "schwere_ratsel", name: "Schwere Rätsel", showLevelDate: false, levelsCount: 20 },
        { type: "profi_ratsel", name: "Profi Rätsel", showLevelDate: false, levelsCount: 20 }
      ]
    },
    {
      type: "wordsearch",
      name: "Wortsuche",
      seoText: { title: "", text: "" },
      categories: [
        { type: "tagliches_ratsel", name: "Tägliches Rätsel", showLevelDate: true, levelsCount: 7 },
        { type: "leichtes", name: "Leichtes", showLevelDate: false, levelsCount: 20 }
      ]
    }
  ],
  appearance: {
    primaryColor: "#ff6b33",
    backgroundColor: "#ffffff",
    textColor: "#020202",
    controlBoxColor: "#f8f8f8",
    onPrimaryTextColor: "#ffffff",
    logo: null
  },
  adProvider: {
    default: {
      videoId: "0_suajnx48",
      partnerId: 103,
      uiConfId: 23466217,
      siteName: {
        desktop: "sta_ratefuchs_rtl",
        mobile: "mob_ratefuchs_rtl"
      }
    },
    rtl: {
      videoId: "0_suajnx48",
      partnerId: 103,
      uiConfId: 23466217,
      siteName: {
        desktop: "sta_ratefuchs_rtl",
        mobile: "mob_ratefuchs_rtl"
      }
    }
  }
};

const API_CONFIG = {
  endpoint: '/game/config/',
  timeout: 10000
};

const ConfigContext = createContext();

export const ConfigProvider = ({ children }) => {
  const [config, setConfig] = useState(defaultConfig);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchConfig = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), API_CONFIG.timeout);

      const response = await fetch(API_CONFIG.endpoint, {
        signal: controller.signal,
        headers: {
          'Content-Type': 'application/json',
        }
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const newConfig = await response.json();
      setConfig(newConfig.data);
      setError(null);
    } catch (err) {
      const errorMessage = err.name === 'AbortError'
        ? 'Request timeout'
        : err.message || 'Failed to load configuration';

      console.error('Config fetch error:', errorMessage);
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  const contextValue = {
    config,
    loading,
    error,
    refreshConfig: fetchConfig,
    isDefaultConfig: config === defaultConfig
  };

  return (
    <ConfigContext.Provider value={contextValue}>
      {children}
    </ConfigContext.Provider>
  );
};

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (!context) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

export const getGameByType = (config, gameType) => {
  return config.games.find(game => game.type === gameType);
};

export const getCategoryByType = (config, gameType, categoryType) => {
  const game = getGameByType(config, gameType);
  return game?.categories.find(category => category.type === categoryType);
};

export default defaultConfig;
